import { ReactElement, useState, useCallback, useMemo, useEffect } from "react";
import { Button, Card, Grid, AgGridReact, AgGrid, Flex, Text, toast } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useFeatureFlag } from "@jsq/flags-react";
import { useUpdateEntityStatusMutation } from "~/graphql/supergraphHooks";
import { type EntityStatus as GraphQLEntityStatus } from "~/graphql/supergraphTypes";
import { useEntityUpdate } from "~/pages/Billing/ClientSpecificView/EntityUpdateContext";
import { useFilterSort } from "~/pages/Billing/ClientSpecificView/FilterSortContext";
import { LoadingCellRenderer } from "~/pages/Billing/components/LoadingCellRenderer";
import {
  BILLING_GRID_NAMES,
  ENTITY_STATUS_FILTER_VALUES,
  PAGINATION,
} from "~/pages/Billing/constants";
import { useBillingDate } from "~/pages/Billing/contexts/BillingDateContext";
import { useGridViewManager } from "~/pages/Billing/hooks/useGridViewManager";
import { SPACING } from "~/styles/variables";
import { EntitiesCardActions } from "../EntitiesCardActions";
import {
  entitiesStatusUpdateInfo,
  entitiesStatusUpdateInfoDescription,
  entitiesTitle,
} from "../styles_vanilla.css";
import {
  billingGroupAutoGroupColumnDef,
  getBillingGroupColumnDefs,
  type EntityStatusColumnParams,
} from "./billingGroupColDef";
import {
  EntitiesByBillingGroupDataSource,
  type BillingGroupEntityRow,
} from "./entitiesByBillingGroupDataSource";
import { gridContainer } from "./styles_vanilla.css";

const sideBarDef: AgGridReact.AgGridReactProps["sideBar"] = {
  toolPanels: [
    {
      id: "columns",
      labelDefault: "Columns",
      labelKey: "columns",
      iconKey: "columns",
      toolPanel: "agColumnsToolPanel",
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
      },
    },
  ],
  defaultToolPanel: "",
};

const updateBillingGroupEntityRow = (
  gridApi: AgGrid.GridApi<BillingGroupEntityRow>,
  updatedEntity: {
    id: number;
    status: string;
  },
): void => {
  if (!gridApi) return;

  const rowNode = gridApi.getRowNode(`entity-${updatedEntity.id}`);

  if (rowNode?.data) {
    const currentRowData = rowNode.data;
    if (currentRowData.type === "entity" && currentRowData.entityData) {
      currentRowData.entityData.entityStatus =
        updatedEntity.status === "ACTIVE" ? "Active" : "Completed";
      currentRowData.entityData.showSpinner = true;
      rowNode.setData(currentRowData);
    }
  }
};

type EntityStatus = NonNullable<BillingGroupEntityRow["entityData"]>["entityStatus"];

const isEntityStatus = (value: unknown): value is EntityStatus => {
  return typeof value === "string" && ENTITY_STATUS_FILTER_VALUES.includes(value);
};

export type EntitiesByBillingGroupGridProps = {
  clientId: string;
  isBillingSaveUserGridViewFlagEnabled: boolean;
  onChangeClientInfo?: () => void;
  onViewHierarchy?: () => void;
};

export const EntitiesByBillingGroupGrid = ({
  clientId,
  isBillingSaveUserGridViewFlagEnabled,
  onChangeClientInfo,
  onViewHierarchy,
}: EntitiesByBillingGroupGridProps): ReactElement => {
  const { setFilters, setSorts } = useFilterSort();
  const { setHasActiveEntityUpdates } = useEntityUpdate();
  const { asOfDateString } = useBillingDate();
  const [gridApi, setGridApi] = useState<AgGrid.GridApi<BillingGroupEntityRow>>();
  const isFCCEnabled = useFeatureFlag("billingFirstCapitalCallDate", false);
  const isSafeCaseIdAndActiveInvestmentsEnabled = useFeatureFlag(
    "billingSafeCaseIdAndActiveInvestments",
    false,
  );

  const handleUpdateEntityStatusCompleted = useCallback(
    (data: {
      updateEntityStatus?: {
        billingRecordCreated: boolean;
        id: number;
        name: string;
        arenaId: number;
        status: string;
      } | null;
    }) => {
      if (data.updateEntityStatus?.billingRecordCreated === false) {
        return toast.error("Entity status update is already in progress");
      }

      if (data.updateEntityStatus) {
        if (gridApi) {
          updateBillingGroupEntityRow(gridApi, data.updateEntityStatus);
        }
        setHasActiveEntityUpdates(true);
        return toast.success("Entity status updated successfully");
      }
    },
    [gridApi, setHasActiveEntityUpdates],
  );

  const [updateEntityStatusMutation, { loading: updateLoading }] =
    useUpdateEntityStatusMutation({
      onCompleted: handleUpdateEntityStatusCompleted,
      onError: (error) => {
        toast.error(`${error.message}`);
      },
    });

  const onEntityStatusChange = useCallback(
    (params: AgGrid.NewValueParams<BillingGroupEntityRow>) => {
      const newStatusValue: unknown = params.newValue;
      const oldStatusValue: unknown = params.oldValue;
      const data = params.data;

      if (!data || data.type !== "entity" || !data.entityId) {
        return;
      }

      if (!isEntityStatus(newStatusValue)) {
        return;
      }

      const newStatus = newStatusValue;
      const oldStatus = isEntityStatus(oldStatusValue) ? oldStatusValue : null;
      const gqlStatus: GraphQLEntityStatus = newStatus === "Active" ? "ACTIVE" : "COMPLETED";

      void updateEntityStatusMutation({
        variables: {
          input: {
            clientId: parseInt(clientId),
            entityId: data.entityId,
            status: gqlStatus,
            data: { old_status: oldStatus, new_status: newStatus },
          },
        },
      });
    },
    [updateEntityStatusMutation, clientId],
  );

  const entityStatusParams: EntityStatusColumnParams = useMemo(
    () => ({
      updateLoading,
      onEntityStatusChange,
    }),
    [updateLoading, onEntityStatusChange],
  );

  const defaultColumnDefs = useMemo(
    () =>
      getBillingGroupColumnDefs(
        entityStatusParams,
        isFCCEnabled,
        isSafeCaseIdAndActiveInvestmentsEnabled,
      ),
    [entityStatusParams, isFCCEnabled, isSafeCaseIdAndActiveInvestmentsEnabled],
  );

  const {
    columnDefs,
    shouldShowSaveViewButton,
    shouldShowClearFilters,
    shouldShowGrid,
    saveViewLoading,
    handleSavedViewClicked,
    handleClearFiltersClicked,
    handleOnColumnMoved,
    handleOnColumnVisible,
    handleOnColumnResized,
    handleOnFilterChanged,
  } = useGridViewManager({
    gridName: BILLING_GRID_NAMES.CLIENT_ARENA_ENTITIES_BY_BILLING_GROUP,
    defaultColumnDefs,
    gridApi,
    isBillingSaveUserGridViewFlagEnabled,
  });

  const dataSource = useMemo(() => {
    if (!clientId) return null;
    return new EntitiesByBillingGroupDataSource(
      clientId,
      {
        onFiltersChange: setFilters,
        onSortsChange: setSorts,
      },
      undefined,
      asOfDateString,
    );
  }, [clientId, asOfDateString, setFilters, setSorts]);

  useEffect(() => {
    if (dataSource) {
      dataSource.setAsOfDate(asOfDateString);
    }
    if (!gridApi || !dataSource) return;
    gridApi.setGridOption("serverSideDatasource", dataSource);
    gridApi.refreshServerSide({ purge: true });
  }, [asOfDateString, gridApi, dataSource]);

  const handleOnGridReady = useCallback((event: AgGrid.GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const getRowId = useCallback((params: AgGrid.GetRowIdParams<BillingGroupEntityRow>) => {
    const data = params.data;
    if (data.type === "billingGroup") {
      return `bg-${data.billingGroupId}`;
    }
    return `entity-${data.entityId}`;
  }, []);

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = useMemo(() => {
    const options: AgGridReact.AgGridReactProps["gridOptions"] = {
      rowModelType: "serverSide",
      suppressServerSideFullWidthLoadingRow: true,
      pagination: true,
      paginationPageSizeSelector: false,
      paginationPageSize: PAGINATION.CLIENT_ENTITIES_BY_BILLING_GROUP_PAGE_SIZE,
      cacheBlockSize: PAGINATION.CLIENT_ENTITIES_BY_BILLING_GROUP_PAGE_SIZE,
      autoGroupColumnDef: billingGroupAutoGroupColumnDef,
      isServerSideGroup: (dataItem: BillingGroupEntityRow) =>
        dataItem.type === "billingGroup" && dataItem.childCount > 0,
      getServerSideGroupKey: (dataItem: BillingGroupEntityRow) => dataItem.billingGroupId,
      groupDisplayType: "singleColumn",
      suppressCellFocus: true,
      sideBar: sideBarDef,
      defaultColDef: {
        menuTabs: ["generalMenuTab", "filterMenuTab"],
      },
      onGridReady: handleOnGridReady,
      onColumnMoved: handleOnColumnMoved,
      onColumnVisible: handleOnColumnVisible,
      onColumnResized: handleOnColumnResized,
      onFilterChanged: handleOnFilterChanged,
    };

    return options;
  }, [
    handleOnGridReady,
    handleOnColumnMoved,
    handleOnColumnVisible,
    handleOnColumnResized,
    handleOnFilterChanged,
  ]);

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center" className={entitiesTitle}>
        <Text variant="h3">Entities</Text>
        <Flex alignItems="center" gap={SPACING.MEDIUM}>
          {onViewHierarchy && (
            <Button
              variant="neutral"
              icon="sitemap"
              iconPosition="left"
              onClick={onViewHierarchy}
            >
              View client to entity hierarchy
            </Button>
          )}
          {onChangeClientInfo && (
            <Button
              variant="neutral"
              icon="arrow-up-right-from-square"
              iconPosition="right"
              onClick={onChangeClientInfo}
            >
              Change client information
            </Button>
          )}
          <EntitiesCardActions
            shouldShowSaveViewButton={shouldShowSaveViewButton}
            shouldShowClearFilters={shouldShowClearFilters}
            isBillingSaveUserGridViewFlagEnabled={isBillingSaveUserGridViewFlagEnabled}
            saveViewLoading={saveViewLoading}
            handleSavedViewClicked={handleSavedViewClicked}
            handleClearFiltersClicked={handleClearFiltersClicked}
          />
        </Flex>
      </Flex>
      <Card className={entitiesStatusUpdateInfo}>
        <Flex gap={SPACING.SMALL} alignItems="center">
          <Icon
            type="info-circle"
            variant="solid"
            color={Colors.Informative600}
            size="large"
            data-testid="entity-status-update-info-icon"
          />
          <Text className={entitiesStatusUpdateInfoDescription}>
            Edits made to the entity status will be visible the following day
          </Text>
        </Flex>
      </Card>
      <div className={gridContainer}>
        {shouldShowGrid && columnDefs && (
          <Grid
            gridOptions={gridOptions}
            columnDefs={columnDefs}
            getRowId={getRowId}
            loadingCellRenderer={LoadingCellRenderer}
          />
        )}
      </div>
    </>
  );
};
